/** Teams, and the categories each one uses.
 *
 * The point of this page is that the taxonomy stops being a code change. Whoever runs a team
 * names its work — Travis for EDEE, Ben for his area — and the change is live immediately and
 * recorded in the audit trail.
 *
 * Two rules the interface makes visible rather than explaining in a tooltip:
 *
 * * **Renaming is safe.** Projects point at an id, so a rename moves nothing. The old
 *   arrangement used the label as the key, which is how "Infrastructure & Modernization"
 *   silently became "Infrastructure" for fourteen projects.
 * * **Nothing is deleted.** A category with projects behind it can only be retired: it leaves
 *   the picker, its projects keep their history, and it can be brought back. A category that
 *   vanished would take its projects out of every grouped view with no explanation.
 */

import { useState } from 'react'
import { Layers, Plus, RotateCcw } from 'lucide-react'

import { Button, Can, ErrorState, Field, Loading, Pill, SectionHeader, inputClass } from '@/components/ui'
import { Permissions } from '@/lib/session'
import {
  useCreateCategory,
  useTeams,
  useUpdateCategory,
  type ProjectCategory,
  type TeamOverview,
} from '@/features/teams/useTeams'

function CategoryRow({ category }: { category: ProjectCategory }) {
  const update = useUpdateCategory()
  const [name, setName] = useState(category.name)
  const dirty = name.trim() !== category.name

  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-2.5">
      <Can
        permission={Permissions.taxonomyManage}
        fallback={<span className="min-w-0 flex-1 text-sm text-fg">{category.name}</span>}
      >
        <input
          className={`${inputClass} min-w-0 flex-1`}
          value={name}
          onChange={(event) => setName(event.target.value)}
          aria-label={`Name of ${category.name}`}
        />
      </Can>

      <span className="text-xs text-fg-muted">
        {category.projectCount} {category.projectCount === 1 ? 'project' : 'projects'}
      </span>

      {category.isActive ? null : <Pill>Retired</Pill>}

      <Can permission={Permissions.taxonomyManage}>
        <div className="flex items-center gap-2">
          {dirty ? (
            <Button
              variant="secondary"
              disabled={update.isPending}
              onClick={() =>
                update.mutate({
                  id: category.id,
                  name: name.trim(),
                  rowVersion: category.rowVersion,
                })
              }
            >
              {update.isPending ? 'Saving…' : 'Rename'}
            </Button>
          ) : null}
          <Button
            variant="secondary"
            disabled={update.isPending}
            title={
              category.isActive
                ? 'Retire this category. Projects keep it and their history; it just leaves the picker.'
                : 'Bring this category back into the picker.'
            }
            onClick={() =>
              update.mutate({
                id: category.id,
                isActive: !category.isActive,
                rowVersion: category.rowVersion,
              })
            }
          >
            {category.isActive ? 'Retire' : <><RotateCcw size={14} aria-hidden /> Restore</>}
          </Button>
        </div>
      </Can>

      {update.error ? (
        <div className="w-full">
          <ErrorState error={update.error} />
        </div>
      ) : null}
    </li>
  )
}

function TeamCard({ team }: { team: TeamOverview }) {
  const create = useCreateCategory()
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')

  const submit = () => {
    const cleaned = name.trim()
    if (!cleaned) return
    create.mutate(
      { team: team.team, name: cleaned },
      {
        onSuccess: () => {
          setName('')
          setAdding(false)
        },
      },
    )
  }

  return (
    <section className="card mb-4">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <Layers size={16} aria-hidden style={{ color: 'var(--brand)' }} />
        <div className="min-w-0 flex-1">
          <div className="font-heading text-base text-fg">{team.label}</div>
          <div className="micro-label text-fg-muted">
            {team.shortLabel} · {team.projectCount} {team.projectCount === 1 ? 'project' : 'projects'}
          </div>
        </div>
        <Can permission={Permissions.taxonomyManage}>
          <Button variant="secondary" onClick={() => setAdding((open) => !open)}>
            <Plus size={14} aria-hidden />
            Add category
          </Button>
        </Can>
      </div>

      {adding ? (
        <div className="flex flex-wrap items-end gap-3 border-b border-border px-4 py-3">
          <div className="min-w-[240px] flex-1">
            <Field label="Category name">
              <input
                className={inputClass}
                value={name}
                autoFocus
                placeholder="e.g. Reporting & Analytics"
                onChange={(event) => setName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') submit()
                  if (event.key === 'Escape') setAdding(false)
                }}
              />
            </Field>
          </div>
          <Button variant="primary" onClick={submit} disabled={create.isPending || !name.trim()}>
            {create.isPending ? 'Adding…' : 'Add'}
          </Button>
          <Button variant="secondary" onClick={() => setAdding(false)}>
            Cancel
          </Button>
          {create.error ? (
            <div className="w-full">
              <ErrorState error={create.error} />
            </div>
          ) : null}
        </div>
      ) : null}

      {team.categories.length === 0 ? (
        <p className="px-4 py-4 text-sm text-fg-muted">
          No categories yet. This team&rsquo;s projects sit under the team itself, which is a
          perfectly good answer — add categories when the work genuinely divides.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {team.categories.map((category) => (
            <CategoryRow key={category.id} category={category} />
          ))}
        </ul>
      )}
    </section>
  )
}

export function TeamsPage() {
  // Retired categories are shown here — this is the only screen that can bring one back.
  const { data, isPending, error } = useTeams(true)

  return (
    <div>
      <h1 className="font-heading text-3xl text-fg">Teams</h1>
      <p className="mt-1 max-w-[70ch] text-sm text-fg-muted">
        Three delivery teams. Each one decides how its own work divides, and a Portfolio Manager
        can change that here without waiting for a release. Renaming a category is safe —
        projects follow it — and nothing is ever deleted while projects still point at it.
      </p>

      <SectionHeader>Teams and categories</SectionHeader>

      {isPending ? (
        <Loading label="Loading teams" />
      ) : error ? (
        <ErrorState error={error} />
      ) : (
        (data?.teams ?? []).map((team) => <TeamCard key={team.team} team={team} />)
      )}
    </div>
  )
}
